import React, { useEffect, useMemo, useState } from "react";
import theme from "assets/theme";
import FaiTypography from "MuiComponents/FaiTypography";
import { Box, Card, CircularProgress } from "@mui/material";
import SearchInput from "components/SearchInput";
import FaiDataGrid from "components/DataGrid/FaiDataGrid";
import { GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import Axios from "services/api/config";
import { getAllReturnsURL } from "services/api/routes/Order";
import dataTableData from "./data/dataTableData";
import ReturnDetails from "./components/ReturnDetails";

interface ReturnRow {
	id: string;
	productName: string;
	qty: number;
	reason: string;
	date: string;
	price: string;
	approvalStatus: string;
	raw: any;
}

// Flatten a backend ReturnedItemResponseDTO into a flat table row.
const toRow = (r: any): ReturnRow => ({
	id: r?.id,
	productName: r?.productName || "-",
	qty: r?.quantity ?? 0,
	reason: r?.returnReason || "-",
	date: r?.orderDate ? new Date(r.orderDate).toLocaleDateString() : "-",
	price: `₹ ${r?.amount ?? 0}`,
	// Returns created before the approval gate have no approvalStatus -> REQUESTED.
	approvalStatus: r?.approvalStatus || "REQUESTED",
	raw: r,
});

function Returns() {
	const { typography } = theme;
	const [orderData, setOrderDetails] = useState<ReturnRow | undefined>();
	const [returns, setReturns] = useState<ReturnRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const [search, setSearch] = useState("");

	const getReturns = async () => {
		try {
			setLoading(true);
			const { data } = await Axios({
				...getAllReturnsURL({
					page: paginationModel.page,
					size: paginationModel.pageSize,
				}),
			});
			const responseData = data?.data || {};
			setReturns((responseData.data || []).map(toRow));
			setRowCount(responseData.totalElements || 0);
		} catch (error) {
			console.error(error);
			setReturns([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getReturns();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationModel.page, paginationModel.pageSize]);

	// Client-side filter over the loaded page (the backend endpoint takes no
	// search parameter), matching product name or reason.
	const visibleRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return returns;
		return returns.filter(
			(r) =>
				r.productName.toLowerCase().includes(q) ||
				r.reason.toLowerCase().includes(q)
		);
	}, [returns, search]);

	const handlePaginationModelChange = (model: GridPaginationModel) => {
		setPaginationModel({ page: model.page, pageSize: model.pageSize });
	};

	const handleRowClick = (params: GridRowParams) => {
		setOrderDetails(params.row as ReturnRow);
	};

	if (orderData) {
		return <ReturnDetails {...{ orderData, setOrderDetails, onRefresh: getReturns }} />;
	}

	return (
		<Card className="my-3 p-3 md:p-5">
			<FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
				Returns
			</FaiTypography>
			<p className="text-sm mb-4">
				You can view and manage all return requests. Track the status of
				returns, process refunds, and handle exchanges efficiently.
			</p>
			<Box className="flex flex-col md:flex-row justify-between items-center my-4 w-full gap-3">
				<SearchInput
					placeholder="Search By Product / Reason"
					value={search}
					onChange={(e: any) => setSearch(e.target.value)}
				/>
			</Box>
			<Box sx={{ mt: 2 }}>
				{loading ? (
					<Box className="flex justify-center p-6">
						<CircularProgress />
					</Box>
				) : (
					<FaiDataGrid
						rows={visibleRows}
						columns={dataTableData.columns}
						pagination={true}
						pageSize={paginationModel.pageSize}
						rowCount={rowCount}
						paginationMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onRowClick={handleRowClick}
					/>
				)}
			</Box>
		</Card>
	);
}

export default Returns;
